import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Link } from "react-router-dom";
import LoadingTable from "../components/LoadingTable";
import { forceRound, getLowerCaseString, loadCss } from "../../helpers/function";
import { ToastBottomEnd } from "../components/Toast";
import {
    toastDeleteErrorMessageConfig,
    toastDeleteSuccessMessageConfig,
    toastErrorMessageWithParameterConfig,
} from "../../config/toastConfig";
import { DeleteConfirmation } from "../components/ConfirmationAlert";
import { confirmDeleteConfig } from "../../config/confirmConfig";
import appConfig from "../../config/appConfig";
import Pagination from "../components/Pagination";
import EmptyTable from "../components/EmptyTable";
import HeaderTablePage from "../components/HeaderTablePage";
import { ERROR_401 } from "../../config/errorPage";
import { verifyPermission } from "../../helpers/permission";
import { NUMBER_ELEMENT_PER_PAGE } from "../../constants/dataTable";
import { verifyTokenExpire } from "../../middleware/verifyToken";
import { useTranslation } from "react-i18next";

loadCss("/assets/plugins/custom/datatables/datatables.bundle.css");

const UnfoundedReason = (props) => {
    const { t, ready, i18n } = useTranslation();
    const currentLang = i18n.language || "fr";

    if (!verifyPermission(props.userPermissions, "list-unfounded-reasons"))
        window.location.href = ERROR_401;

    const listEndpoint = `${appConfig.apiDomaine}/unfounded-reasons`;
    const [load, setLoad] = useState(true);
    const [unfoundedReasons, setUnfoundedReasons] = useState([]);
    const [numberPerPage, setNumberPerPage] = useState(NUMBER_ELEMENT_PER_PAGE);
    const [activeNumberPage, setActiveNumberPage] = useState(1);
    const [numberPage, setNumberPage] = useState(0);
    const [showList, setShowList] = useState([]);

    useEffect(() => {
        async function fetchData() {
            await axios
                .get(listEndpoint)
                .then((response) => {
                    const data = response.data || [];
                    setNumberPage(forceRound(data.length / NUMBER_ELEMENT_PER_PAGE));
                    setShowList(data.slice(0, NUMBER_ELEMENT_PER_PAGE));
                    setUnfoundedReasons(data);
                    setLoad(false);
                })
                .catch(() => {
                    setLoad(false);
                });
        }
        if (verifyTokenExpire()) fetchData();
    }, [listEndpoint]);

    const getLibelle = (item) => {
        if (!item || !item.libelle) return "-";
        if (typeof item.libelle === "string") return item.libelle;
        return item.libelle[currentLang] || item.libelle["fr"] || item.libelle["en"] || "-";
    };

    const filterShowListBySearchValue = (value) => {
        value = getLowerCaseString(value);
        return unfoundedReasons.filter((el) =>
            getLowerCaseString(getLibelle(el)).indexOf(value) >= 0
        );
    };

    const searchElement = (e) => {
        const val = e.target.value;
        if (val) {
            const filtered = filterShowListBySearchValue(val);
            setNumberPage(forceRound(filtered.length / NUMBER_ELEMENT_PER_PAGE));
            setShowList(filtered.slice(0, NUMBER_ELEMENT_PER_PAGE));
        } else {
            setNumberPage(forceRound(unfoundedReasons.length / NUMBER_ELEMENT_PER_PAGE));
            setShowList(unfoundedReasons.slice(0, NUMBER_ELEMENT_PER_PAGE));
            setActiveNumberPage(1);
        }
    };

    const onChangeNumberPerPage = (e) => {
        const count = parseInt(e.target.value);
        setActiveNumberPage(1);
        setNumberPerPage(count);
        setShowList(unfoundedReasons.slice(0, count));
        setNumberPage(forceRound(unfoundedReasons.length / count));
    };

    const getEndByPosition = (position) => position * numberPerPage;

    const onClickPage = (e, page) => {
        e.preventDefault();
        setActiveNumberPage(page);
        setShowList(
            unfoundedReasons.slice(
                getEndByPosition(page) - numberPerPage,
                getEndByPosition(page)
            )
        );
    };

    const deleteUnfoundedReason = (id, index) => {
        DeleteConfirmation.fire(confirmDeleteConfig()).then((result) => {
            if (verifyTokenExpire() && result.value) {
                axios
                    .delete(`${appConfig.apiDomaine}/unfounded-reasons/${id}`)
                    .then(() => {
                        const newList = [...unfoundedReasons];
                        newList.splice(index, 1);
                        setUnfoundedReasons(newList);
                        setShowList(newList.slice(0, numberPerPage));
                        setNumberPage(forceRound(newList.length / numberPerPage));
                        ToastBottomEnd.fire(toastDeleteSuccessMessageConfig());
                    })
                    .catch((error) => {
                        if (error.response && error.response.data && error.response.data.error) {
                            ToastBottomEnd.fire(toastErrorMessageWithParameterConfig(error.response.data.error));
                        } else {
                            ToastBottomEnd.fire(toastDeleteErrorMessageConfig());
                        }
                    });
            }
        });
    };

    return ready ? (
        <div className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor" id="kt_content">
            <div className="kt-subheader kt-grid__item" id="kt_subheader">
                <div className="kt-container kt-container--fluid">
                    <div className="kt-subheader__main">
                        <h3 className="kt-subheader__title">{t("Paramètres")}</h3>
                        <span className="kt-subheader__separator kt-hidden" />
                        <div className="kt-subheader__breadcrumbs">
                            <a href="#home" className="kt-subheader__breadcrumbs-home"><i className="flaticon2-shelter" /></a>
                            <span className="kt-subheader__breadcrumbs-separator" />
                            <span className="kt-subheader__breadcrumbs-link">{t("Motifs de non-fondé")}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
                <div className="kt-portlet">
                    <HeaderTablePage
                        addPermission="store-unfounded-reasons"
                        title={t("Motifs de non-fondé")}
                        addText={t("Ajouter un motif")}
                        addLink="/settings/unfounded-reasons/add"
                    />
                    <div className="px-4 pt-2 text-right">
                        {verifyPermission(props.userPermissions, "store-unfounded-reasons") && (
                            <Link to="/settings/unfounded-reasons/import" className="btn btn-sm btn-label-brand btn-bold">
                                <i/> {t("Importation Excel")}
                            </Link>
                        )}
                    </div>

                    {load ? (
                        <LoadingTable />
                    ) : (
                        <div className="kt-portlet__body">
                            <div className="dataTables_wrapper dt-bootstrap4">
                                <div className="row mb-3">
                                    <div className="col-sm-6 text-left">
                                        <label>
                                            {t("Recherche")}:
                                            <input
                                                type="text"
                                                onKeyUp={searchElement}
                                                className="form-control form-control-sm"
                                                placeholder=""
                                            />
                                        </label>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-12">
                                        <table className="table table-striped table-bordered table-hover">
                                            <thead>
                                                <tr>
                                                    <th>{t("Libellé du motif")}</th>
                                                    <th style={{ width: "120px" }}>{t("Action")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {unfoundedReasons.length ? (
                                                    showList.length ? (
                                                        showList.map((item, index) => (
                                                            <tr key={item.id || index}>
                                                                <td>{getLibelle(item)}</td>
                                                                <td>
                                                                    {verifyPermission(props.userPermissions, "update-unfounded-reasons") && (
                                                                        <Link
                                                                            to={`/settings/unfounded-reasons/${item.id}/edit`}
                                                                            className="btn btn-sm btn-clean btn-icon btn-icon-md"
                                                                            title={t("Modifier")}
                                                                        >
                                                                            <i className="la la-edit" />
                                                                        </Link>
                                                                    )}
                                                                    {verifyPermission(props.userPermissions, "destroy-unfounded-reasons") && (
                                                                        <button
                                                                            onClick={() => deleteUnfoundedReason(item.id, index)}
                                                                            className="btn btn-sm btn-clean btn-icon btn-icon-md"
                                                                            title={t("Supprimer")}
                                                                        >
                                                                            <i className="la la-trash" />
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <EmptyTable search={true} />
                                                    )
                                                ) : (
                                                    <EmptyTable />
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-12 col-md-5">
                                        <div className="dataTables_info">
                                            {t("Affichage de")} {showList.length} {t("sur")} {unfoundedReasons.length} {t("données")}
                                        </div>
                                    </div>
                                    {showList.length ? (
                                        <div className="col-sm-12 col-md-7 dataTables_pager">
                                            <Pagination
                                                numberPerPage={numberPerPage}
                                                onChangeNumberPerPage={onChangeNumberPerPage}
                                                activeNumberPage={activeNumberPage}
                                                onClickPreviousPage={(e) => onClickPage(e, activeNumberPage - 1)}
                                                pages={Array.from({ length: numberPage }, (_, i) => i)}
                                                onClickPage={onClickPage}
                                                numberPage={numberPage}
                                                onClickNextPage={(e) => onClickPage(e, activeNumberPage + 1)}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    ) : null;
};

const mapStateToProps = (state) => ({
    userPermissions: state.user.user.permissions,
});

export default connect(mapStateToProps)(UnfoundedReason);